// Asking the engine about a position: what it is worth, and what it plays.

import { getJson, isSignedOut, sendJson, sleep } from "./api.ts";
import type { EngineAnswer } from "../board/puzzle.ts";
import type { Score } from "../board/lines.ts";

// One question per position, however many ask it: a board that wants the
// engine's move and the strip under it that wants the score are asking the
// same thing of the same position at the same moment.
const answers = new Map<string, Promise<{ line?: string[]; white_eval?: Score | null }>>();

export function askPosition(fen: string): Promise<{ line?: string[]; white_eval?: Score | null }> {
  const known = answers.get(fen);
  if (known) return known;
  const asking = (async () => {
    let result = await sendJson("POST", "/api/position", { fen });
    while (!result.ready) {
      const job = await getJson(`/api/jobs/${result.job_id}`);
      if (job.status === "failed") throw new Error(job.error || "The engine could not search that position.");
      if (job.status === "done") return job.result || {};
      await sleep(900);
    }
    return result;
  })();
  answers.set(fen, asking);
  // A failure is not an answer: the next one to ask asks again.
  asking.catch(() => answers.delete(fen));
  if (answers.size > 200) answers.delete(answers.keys().next().value!);
  return asking;
}

/** What a position is worth, for the strip under a board. */
export async function scoreOf(fen: string): Promise<Score | null> {
  try {
    return (await askPosition(fen)).white_eval || null;
  } catch {
    return null;
  }
}

/** The engine's line from a position someone reached by trying a move. */
export async function engineLine(fen: string): Promise<EngineAnswer> {
  try {
    return await askPosition(fen);
  } catch (err) {
    return { error: isSignedOut(err) ? "Sign in to ask the engine." : String(err instanceof Error ? err.message : err) };
  }
}
